import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import asLogoWhite from '../../assets/img/as-logo-white.svg';
import supportIcon from '../../assets/img/support.svg';
import { logout } from '../../services/api/AuthService';
import { fetchActiveNotices } from '../../services/api/NoticeService';
import '../../styles/Header.css';
import { formatDateTimeForDisplay } from '../../utils/dateFormatter';

const MOBILE_BREAKPOINT = 768;
const MENU_CLOSE_DELAY = 200;
const Header = ({ onMenuToggle, menuItems = [] }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [userType, setUserType] = useState('');
  const [userInfo, setUserInfo] = useState({});
  const [supportMenuOpen, setSupportMenuOpen] = useState(false);
  const [noticeMenuOpen, setNoticeMenuOpen] = useState(false);
  const [notices, setNotices] = useState([]);
  const supportMenuRef = useRef(null);
  const noticeMenuRef = useRef(null);
  const closeTimerRef = useRef(null);
  const isSolicitorPath = location.pathname.startsWith('/solicitor');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= MOBILE_BREAKPOINT);

  useEffect(() => {
    const savedUserType = localStorage.getItem('userType');
    setUserType(savedUserType || '');
    const info = localStorage.getItem('userInfo');
    setUserInfo(info ? JSON.parse(info) : {});

    const handleResize = () => {
      setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    };

    handleResize(); // 初期値設定
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const supportDropdown = document.querySelector('.support-menu-dropdown');
      const noticeDropdown = document.querySelector('.notice-menu-dropdown');

      const isOutsideSupport =
        !supportMenuRef.current?.contains(event.target) &&
        !supportDropdown?.contains(event.target);

      const isOutsideNotice =
        !noticeMenuRef.current?.contains(event.target) &&
        !noticeDropdown?.contains(event.target);

      if (isOutsideSupport) {
        setSupportMenuOpen(false);
      }
      if (isOutsideNotice) {
        setNoticeMenuOpen(false);
      }
    };

    if (supportMenuOpen || noticeMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
    };
  }, [supportMenuOpen, noticeMenuOpen]);

  useEffect(() => {
    const loadActiveNotices = async () => {
      if (!isSolicitorPath) {
        setNotices([]);
        return;
      }

      try {
        const response = await fetchActiveNotices();
        const noticeList = Array.isArray(response)
          ? response
          : Array.isArray(response?.notices)
            ? response.notices
            : [];
        setNotices(noticeList);
      } catch (error) {
        console.error('Error fetching active notices:', error);
        setNotices([]);
      }
    };

    loadActiveNotices();
  }, [isSolicitorPath]);

  // ロゴクリック時にトップページへ遷移
  const handleLogoClick = () => {
    switch (userType) {
      case 'admin':
        navigate('/admin/agency/search');
        break;
      case 'agency': {
        const agencyId = userInfo.agency_id;
        if (agencyId) {
          navigate(`/agency/detail/${agencyId}`, {
            state: { initialTab: 'info' }
          });
        } else {
          navigate('/');
        }
        break;
      }
      case 'solicitor':
        navigate('/solicitor/insurance/system/search');
        break;
      default:
        navigate('/');
    }
  };

  const toggleMenu = () => {
    const newMenuState = !menuOpen;
    setMenuOpen(newMenuState);
    onMenuToggle?.(newMenuState);
  };

  // ユーザータイプに基づいて表示するテキストを決定
  const getUserTypeText = () => {
    switch (userType) {
      case 'admin':
        return 'ー IRRC管理者 ー';
      case 'agency':
        return 'ー 代理店管理者 ー';
      case 'solicitor':
        return ' ';
      default:
        return '';
    }
  };

  const handleLogout = async () => {
    await logout();
    localStorage.removeItem('token');
    localStorage.removeItem('userInfo');
    navigate(`/`);
  };

  const toggleNoticeMenu = () => {
    setNoticeMenuOpen(!noticeMenuOpen);
  };

  const isNoticeInPeriod = (notice) => {
    const now = new Date();

    if (!notice?.notice_from || !notice?.notice_to) {
      return false;
    }

    const noticeFrom = new Date(notice.notice_from);
    const noticeTo = new Date(notice.notice_to);

    if (Number.isNaN(noticeFrom.getTime()) || Number.isNaN(noticeTo.getTime())) {
      return false;
    }

    return noticeFrom <= now && now <= noticeTo;
  };

  const hasNoticeInPeriod = notices.some((notice) => isNoticeInPeriod(notice));
  // サポートリンク
  const supportLinks = [
    {
      label: '利用規約',
      url: 'https://hs.irrc.co.jp/asfinder/useragreement.pdf'
    },
    {
      label: '掲載文書一覧',
      url: 'https://hs.irrc.co.jp/asfinder/list.pdf'
    },
    {
      label: 'マニュアル',
      url: 'https://go.irrc.co.jp/asfinder_manual'
    }
  ];
  // サポートメニュー制御
  const toggleSupportMenu = () => {
    setSupportMenuOpen(!supportMenuOpen);
  };
  const handleMouseEnter = () => {
    if (window.innerWidth > MOBILE_BREAKPOINT) {
      // タイマーをクリア
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
      setSupportMenuOpen(true);
    }
  };
  const handleMouseLeave = (event) => {
    if (window.innerWidth > MOBILE_BREAKPOINT) {
      // イベントが存在する場合のみ relatedTarget をチェック
      if (event && event.relatedTarget) {
        const relatedTarget = event.relatedTarget;
        // サポートメニュー内の要素に移動する場合は何もしない
        if (relatedTarget && (
          supportMenuRef.current?.contains(relatedTarget) ||
          relatedTarget.closest('.support-menu-dropdown')
        )) {
          return;
        }
      }
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
      closeTimerRef.current = setTimeout(() => {
        setSupportMenuOpen(false);
      }, MENU_CLOSE_DELAY);
    }
  };

  return (
    <div className={`header-container ${menuOpen ? 'menu-open' : ''}`}>
      <header className="app-header">
        <div className="header-left">
          <button
            className={`menu-button ${menuOpen ? 'open' : ''}`}
            onClick={toggleMenu}
            aria-label="メニューを開く"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
          <div
            className="header-logo"
            onClick={handleLogoClick}
            style={{ cursor: 'pointer' }}
          >
            <div className='header-logo-box'>
              <img src={asLogoWhite} alt="IRC 株式会社アイリックコーポレーション" />
              <span className='header-logo-box-text'>{getUserTypeText()}</span>
            </div>
          </div>
        </div>
        <div className="header-right">
          {isSolicitorPath && (
            <div className="notice-menu-container" ref={noticeMenuRef}>
              <button
                className="notice-bell-button"
                onClick={toggleNoticeMenu}
                aria-expanded={noticeMenuOpen}
                aria-haspopup="true"
                aria-label="おしらせ一覧"
              >
                <span className="dli-bell" aria-hidden="true"></span>
                {hasNoticeInPeriod && <span className="notice-bell-badge" aria-hidden="true"></span>}
              </button>
              {noticeMenuOpen && noticeMenuRef.current && ReactDOM.createPortal(
                <div
                  className="notice-menu-dropdown"
                  style={isMobile ? {
                    position: 'fixed',
                    top: noticeMenuRef.current.getBoundingClientRect().bottom + 4,
                    right: 0
                  } : {
                    position: 'fixed',
                    top: noticeMenuRef.current.getBoundingClientRect().bottom + 4,
                    right: window.innerWidth - noticeMenuRef.current.getBoundingClientRect().right
                  }}
                >
                  <div className="notice-menu-title">おしらせ</div>
                  <div className="notice-list">
                    {notices.length === 0 ? (
                      <div className="notice-empty">現在表示可能なおしらせはありません。</div>
                    ) : (
                      notices.map((notice, index) => {
                        const isActiveNotice = isNoticeInPeriod(notice);
                        const addedAt = notice.created_at || notice.published_from;
                        return (
                          <article
                            className={`notice-card ${isActiveNotice ? 'notice-card-active' : ''}`}
                            key={notice.id ?? `${notice.title}-${index}`}
                          >
                            <header className="notice-card-header">
                              <div className="notice-card-meta">
                                <div className="notice-card-meta meta-left">
                                {isActiveNotice && (
                                  <span className="notice-card-active-dot" aria-label="通知期間中">●</span>
                                )}
                                <span className="dli-clock" aria-hidden="true"></span>
                                <span className="notice-card-added-at">
                                  {formatDateTimeForDisplay(addedAt)}
                                </span>
                                </div>
                                <div className="notice-card-meta meta-right">
                                <span className="notice-card-title">{notice.title || '-'}</span>
                                </div>
                              </div>

                            </header>
                            <div className="notice-card-body">{notice.content || '-'}</div>
                          </article>
                        );
                      })
                    )}
                  </div>
                </div>,
                document.body
              )}
            </div>
          )}

          {/* サポートドロップダウンメニュー */}
          {userType === 'solicitor' && (
            <div
              className="support-menu-container"
              ref={supportMenuRef}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <button
                className="support-menu-button"
                onClick={toggleSupportMenu}
                aria-expanded={supportMenuOpen}
                aria-haspopup="true"
                aria-label="サポートメニュー"
              >
                <img
                  src={supportIcon}
                  alt=""
                  aria-hidden="true"
                  className={`support-menu-icon ${supportMenuOpen ? 'open' : ''}`}
                />
                {!isMobile && 'メニュー'}
              </button>
              {supportMenuOpen && supportMenuRef.current && ReactDOM.createPortal(
                <div
                  className="support-menu-dropdown"
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                  style={{
                    position: 'fixed',
                    top: supportMenuRef.current.getBoundingClientRect().bottom + 4,
                    right: window.innerWidth - supportMenuRef.current.getBoundingClientRect().right
                  }}
                >
                  {/* モバイル用の閉じるボタン */}
                  {window.innerWidth <= MOBILE_BREAKPOINT && (
                    <button
                      className="support-menu-close"
                      onClick={() => setSupportMenuOpen(false)}
                      aria-label="メニューを閉じる"
                    >
                      ×
                    </button>
                  )}
                  {supportLinks.map((link, index) => (
                    <a
                      key={index}
                      href={link.url}
                      className="support-menu-link"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setSupportMenuOpen(false)}
                    >
                      {link.label}
                    </a>
                  ))}
                </div>,
                document.body
              )}
            </div>
          )}
          <button className="logout-button" onClick={handleLogout}>ログアウト</button>
        </div>
      </header>

      <div className={`sidebar-menu ${menuOpen ? 'open' : ''}`}>
        <ul>
          {menuItems.map((item, index) => (
            <li
              key={index}
              onClick={() => {
                if (item.path) {
                  navigate(item.path);
                }
                if (item.onClick) {
                  item.onClick();
                }
                // メニュー項目をクリックしたらメニューを閉じる
                setMenuOpen(false);
              }}
            >
              {item.label}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default Header;
